// hooks/usePostStatus.ts
import { useState, useCallback } from "react";
import { postsService } from "../services/postsService";
import { PostStatus, StatusUpdateResponse } from "@/core/types/postTypes";
import { useAppStore } from "@/core/store/useAppStore";
import { postCacheUtils } from "./usePostsQuery";
import { PostItem } from "@/core/Post/PostItem.model";
import { UserRole } from "@/services/urlService";

interface UsePostStatusOptions {
  onStatusUpdate?: (response: StatusUpdateResponse) => void;
  onError?: (error: string) => void;
}

export const usePostStatus = (options: UsePostStatusOptions = {}) => {
  const selectedClient = useAppStore((state) => state.selectedClient);
  const [error, setError] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  const updateStatus = useCallback(
    async (
      postId: string,
      newStatus: PostStatus,
    ): Promise<StatusUpdateResponse | null> => {
      if (!selectedClient?.id) {
        const errorMsg = "No client selected";
        setError(errorMsg);
        options.onError?.(errorMsg);
        return null;
      }

      setError(null);
      setIsUpdating(true);

      try {
        // Call the API to update the status
        const response = await postsService.updatePostStatus(
          selectedClient.id,
          postId,
          newStatus,
        );

        // Update the React Query cache
        const postItem = PostItem.fromLegacyPost(response.post, UserRole.ADMIN, selectedClient.id);
        postCacheUtils.upsertPost(selectedClient.id, postItem);

        options.onStatusUpdate?.(response);
        return response;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Failed to update status";
        setError(errorMessage);
        options.onError?.(errorMessage);
        return null;
      } finally {
        setIsUpdating(false);
      }
    },
    [selectedClient?.id, options],
  );

  return {
    updateStatus,
    isUpdating,
    error,
    clearError: () => setError(null),
  };
};
